import React, { useState } from "react";
import {
  ActivityIndicator,
  Alert,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { Fontisto, Ionicons } from "@expo/vector-icons";

import UserNotificationBaseItem from "./UserNotificationBaseItem";

import type {
  UserNotification,
  UserNotificationWinkData,
} from "@/schemas/user/notification";
import { themes } from "@/Components/Styling/themeConfigs";
import InteractionService from "@/services/user/interaction";
import type { AppUserSession } from "@/schemas/auth/session";
import { formatTimeRemaining, isExpired } from "@/formatting/datetime";

type Props = {
  session: AppUserSession;
  userNotification: UserNotification;
  handleMarkNotificationAsRead: () => void;
};

function UserNotificationWinkItem({
  session,
  userNotification,
  handleMarkNotificationAsRead,
}: Props): React.JSX.Element {
  const [isSendingWink, setIsSendingWink] = useState(false);
  const [hasWinkedBack, setHasWinkedBack] = useState(false);

  const now = new Date();
  const WINK_EXPIRATION_THRESHOLD = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
  const creationDate = userNotification.createdAt;
  const expirationDate = new Date(
    creationDate.getTime() + WINK_EXPIRATION_THRESHOLD,
  );
  const isMoreThanThreshold = isExpired(expirationDate);

  const currentTheme = themes.morning;
  const notificationData = userNotification.data as UserNotificationWinkData;
  const profilePreview = notificationData.winkSent.sentBy;

  const sendWink = async (): Promise<void> => {
    setIsSendingWink(true);
    const interactionService = new InteractionService(session);
    try {
      await interactionService.sendWink(profilePreview.username);
      Alert.alert("Wink Sent! 😉", "✨ Your wink has been sent!", [
        { text: "Awesome!" },
      ]);
      setHasWinkedBack(true);
    } catch (error) {
      Alert.alert("Uh-oh!", "Failed to send your wink. Try again later?", [
        { text: "Sure!" },
      ]);
    } finally {
      setIsSendingWink(false);
    }
  };

  const styles = StyleSheet.create({
    itemRightSection: {
      flex: 1,
      flexDirection: "column",
      alignItems: "flex-end",
      justifyContent: "center",
    },
    itemExpirationInfoContainer: {
      marginTop: 3,
      flexDirection: "row",
      alignItems: "center",
    },
    itemExpirationText: {
      fontSize: currentTheme.typography.bodySmall.size.mobile,
      color: "#444",
    },
    baseButton: {
      borderRadius: 20,
      marginLeft: "auto",
      paddingVertical: 5,
      paddingHorizontal: 15,
      borderWidth: 1,
      minHeight: 40,
      minWidth: 80,
      alignItems: "center",
      justifyContent: "center",
      borderColor: isMoreThanThreshold ? "gray" : "black",
    },
    baseButtonText: {
      fontSize: currentTheme.typography.bodySmall.size.mobile,
      fontWeight: 600,
      color: isMoreThanThreshold ? "gray" : "black",
    },
  });

  // TODO?: https://github.com/dow-labs/Kai-Mobile/pull/700#discussion_r2080258202
  return (
    <UserNotificationBaseItem
      renderRightComponent={(markAsRead) => (
        <View style={styles.itemRightSection}>
          {/* wink button */}
          <Pressable
            disabled={isMoreThanThreshold || isSendingWink || hasWinkedBack}
            onPress={() => {
              markAsRead();
              sendWink();
            }}
            style={styles.baseButton}
          >
            {isSendingWink ? (
              <ActivityIndicator size="small" />
            ) : (
              <>
                <View>
                  <Text style={styles.baseButtonText}>
                    {isMoreThanThreshold
                      ? "Expired"
                      : hasWinkedBack
                        ? "✔ Winked Back"
                        : "Wink Back"}
                  </Text>
                </View>
              </>
            )}
          </Pressable>
          {/* expiration info */}
          {!hasWinkedBack && (
            <View style={styles.itemExpirationInfoContainer}>
              {isMoreThanThreshold ? (
                <Text style={styles.itemExpirationText}>
                  Wink expired after 24h
                </Text>
              ) : (
                <>
                  <Ionicons
                    name="timer-outline"
                    size={currentTheme.typography.bodySmall.size.mobile}
                  />
                  <Text style={styles.itemExpirationText}>
                    {" "}
                    Expires in {formatTimeRemaining(now, expirationDate)}
                  </Text>
                </>
              )}
            </View>
          )}
        </View>
      )}
      userNotification={userNotification}
      profile={profilePreview}
      handleMarkNotificationAsRead={handleMarkNotificationAsRead}
      notificationTypeIconComponent={
        <Fontisto
          name="wink"
          size={17}
          color={currentTheme.button.color.primary}
        />
      }
      notificationDescription={"sent you a wink"}
    />
  );
}

export default UserNotificationWinkItem;
